import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import LegalLayout from '../components/LegalLayout';
import { SUPPORT_EMAIL } from '../config/support';

// ---------------------------------------------------------------------------
// Public account deletion request page (audit C6 / Finding 4.8a).
//
// Google Play requires a web-accessible deletion request URL in addition to
// the in-app deletion path, reachable WITHOUT logging in - it is checked by a
// reviewer who has no account. Keep this route public and keep it linked from
// the privacy policy and the footers.
//
// Submitting here deletes nothing. It emails support so a human can verify
// ownership first. If email is unavailable the form falls back to a prefilled
// mailto link so the request can still be made.
// ---------------------------------------------------------------------------

const API = process.env.REACT_APP_API_URL;

function DeleteRequest() {
  const [form, setForm] = useState({ email: '', companyName: '', note: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | failed
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (status === 'failed') {
      setStatus('idle');
      setMessage('');
    }
  };

  // Prefilled fallback, used when the API cannot take the request.
  const mailtoHref = () => {
    const body = [
      'I am requesting deletion of my SnowTrack™ account.',
      '',
      `Account email: ${form.email || '(your account email)'}`,
      `Company name: ${form.companyName || '(your company name)'}`,
      form.note ? `Note: ${form.note}` : '',
    ].filter(Boolean).join('\n');
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Account Deletion Request')}&body=${encodeURIComponent(body)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setMessage('');

    try {
      const res = await axios.post(`${API}/api/account/deletion-request`, {
        email: form.email.trim(),
        companyName: form.companyName.trim(),
        note: form.note.trim(),
      });
      setStatus('sent');
      setMessage(res.data?.message || 'Your deletion request has been received.');
    } catch (err) {
      const data = err.response?.data;
      setStatus('failed');
      setMessage(
        data?.error ||
        'We could not submit your request. Please email us directly using the link below.'
      );
    }
  };

  return (
    <LegalLayout
      title="Delete My Account"
      subtitle="Request deletion of your SnowTrack™ account and all the data in it."
    >
      <div className="legal-callout">
        <p>
          <strong>Already logged in?</strong> You do not need this form. A company owner can
          delete the account immediately from <strong>Settings &rarr; Delete Account</strong>.
          Use this page if you cannot log in, or if you would rather we handled it.
        </p>
      </div>

      <h2>What gets deleted</h2>
      <p>Deleting an account permanently removes, for that company:</p>
      <ul>
        <li>The company record and its settings</li>
        <li>All user accounts, including owners, admins, crew leads and drivers</li>
        <li>All customers and their properties</li>
        <li>All routes, route stops and service history</li>
        <li>All invoices, payment records and expenses</li>
        <li>All uploaded photos</li>
      </ul>
      <p>
        This cannot be undone. <strong>Export anything you need first.</strong> Deleted data is
        removed from the live database immediately and rolls out of encrypted backups within 30
        days. See our <Link to="/privacy">Privacy Policy</Link> for the detail.
      </p>

      <h2>Request deletion</h2>
      <p>
        Tell us the email address on the account and the company name. We verify that you own
        the account before deleting anything, and we aim to reply within five business days.
      </p>

      {status === 'sent' ? (
        <div className="dr-success">
          <h3>Request received</h3>
          <p>{message}</p>
          <p>
            We will contact you at <strong>{form.email}</strong> to confirm before anything is
            deleted.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="dr-form">
          <div className="form-group">
            <label className="form-label" htmlFor="dr-email">Account email address</label>
            <input
              type="email"
              id="dr-email"
              name="email"
              className="form-input"
              value={form.email}
              onChange={handleChange}
              placeholder="you@company.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="dr-company">Company name</label>
            <input
              type="text"
              id="dr-company"
              name="companyName"
              className="form-input"
              value={form.companyName}
              onChange={handleChange}
              placeholder="Your Business Name"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="dr-note">
              Anything else we should know? <span className="dr-optional">(optional)</span>
            </label>
            <textarea
              id="dr-note"
              name="note"
              className="form-textarea"
              rows={3}
              value={form.note}
              onChange={handleChange}
              placeholder="For example: I no longer have access to this email address."
            />
          </div>

          {status === 'failed' && (
            <div className="alert alert-error" aria-live="polite">
              <p>{message}</p>
              <p>
                <a href={mailtoHref()}>Email {SUPPORT_EMAIL} instead</a>, and the message will
                be prefilled with what you entered.
              </p>
            </div>
          )}

          <button type="submit" className="btn btn-danger btn-lg" disabled={status === 'sending'}>
            {status === 'sending' ? 'Submitting...' : 'Request Account Deletion'}
          </button>
        </form>
      )}

      <h2>Prefer to email us?</h2>
      <p>
        Send your account email address and company name to{' '}
        <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Account Deletion Request')}`}>
          {SUPPORT_EMAIL}
        </a>{' '}
        with the subject &quot;Account Deletion Request&quot;. That reaches the same place.
      </p>

      <style>{`
        .dr-form {
          margin: 0 0 24px;
          max-width: 480px;
        }

        .dr-optional {
          color: #9ca3af;
          font-weight: 400;
        }

        .dr-success {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-left: 4px solid #16a34a;
          border-radius: 8px;
          padding: 20px;
          margin: 0 0 24px;
        }

        .dr-success h3 {
          margin-top: 0;
          color: #166534;
        }

        .dr-success p:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </LegalLayout>
  );
}

export default DeleteRequest;
